import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerApi, activityApi, userApi, projectApi, documentApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import Badge from '../components/Badge.jsx';
import Spinner from '../components/Spinner.jsx';
import EmptyState from '../components/EmptyState.jsx';
import ScoreGauge from '../components/ScoreGauge.jsx';
import LeadBrief from '../components/LeadBrief.jsx';
import CommentThread from '../components/CommentThread.jsx';
import Modal from '../components/Modal.jsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.jsx';
import { formatCurrency, formatDate, formatDateTime, fullName } from '../utils/format.js';
import { CUSTOMER_STATUSES, CAN_ASSIGN_ROLES, DELIVERY_ROLES, CAN_BILL_ROLES } from '../constants.js';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  // The 33 answers are worth having, but not worth scrolling past every time.
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const locale = i18n.resolvedLanguage;
  const { data: customer, loading, error, refresh } = useApi(() => customerApi.get(id, locale), [id, locale]);
  // Waits for the customer: on an answer-only record the timeline is neither
  // shown nor allowed, so asking for it would only be a 403 on every open.
  const { data: activities } = useApi(
    () =>
      customer && !customer.answerOnly
        ? activityApi.list({ customerId: id, pageSize: 30 })
        : Promise.resolve(null),
    [id, customer],
  );
  const { data: directory } = useApi(() => userApi.directory(), []);

  // Only the delivery roles hold PROJECT_READ; for anyone else the call would
  // be a 403 and the card would have nothing to show.
  const seesProjects = DELIVERY_ROLES.includes(user.role);
  const { data: projects } = useApi(
    () => (seesProjects ? projectApi.list({ customerId: id, pageSize: 50 }) : Promise.resolve(null)),
    [id],
  );

  // Quotes and invoices: sales and administration. The API scopes what comes
  // back, so this only decides whether the card is worth drawing.
  const canBill = CAN_BILL_ROLES.includes(user.role);
  const { data: docs, refresh: refreshDocs } = useApi(
    () => (canBill ? documentApi.list({ customerId: id, pageSize: 50 }) : Promise.resolve(null)),
    [id],
  );

  const canEdit = ['TELEPRO', 'COMMERCIAL', 'ADMIN'].includes(user.role);
  // Still a prospect: nobody has run the script on it yet, so offer the call.
  const canCall = user.role === 'TELEPRO' && !customer?.brief;
  // Showing a whole card of dropdowns you are not allowed to touch is noise.
  const canAssign = CAN_ASSIGN_ROLES.includes(user.role);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={toMessage(error)} />;

  /**
   * ANSWER-ONLY VIEW.
   *
   * IT has been called into a thread on an account that is not its own. It gets
   * the question and the reply box, and nothing else - the API already stripped
   * the record (see `answerOnlyView`). Falling through to the normal page would
   * draw a row of empty cards and put back exactly the noise the signature
   * gate exists to take off this team's screens.
   */
  if (customer.answerOnly) {
    return (
      <>
        <PageHeader
          title={customer.companyName}
          subtitle={[customer.city, customer.country].filter(Boolean).join(', ')}
          actions={
            <button type="button" className="btn" onClick={() => navigate(-1)}>
              {t('common.back')}
            </button>
          }
        />
        <Card title={t('customers.answerOnly')}>
          <p className="card__lead">{t('customers.answerOnlyHint')}</p>
          <dl className="detail-list">
            <div>
              <dt>{t('customers.industry')}</dt>
              <dd>{customer.industry || '-'}</dd>
            </div>
            <div>
              <dt>{t('customers.website')}</dt>
              <dd>{customer.website || '-'}</dd>
            </div>
          </dl>
        </Card>
        <CommentThread customerId={id} highlightId={searchParams.get('comment')} />
      </>
    );
  }

  const startEdit = () => {
    setDraft({
      status: customer.status,
      estimatedValue: customer.estimatedValue,
      email: customer.email,
      phone: customer.phone,
      city: customer.city,
      country: customer.country,
      notes: customer.notes,
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await customerApi.update(id, { ...draft, estimatedValue: Number(draft.estimatedValue) || 0 });
      toast.success(t('common.save'));
      setEditing(false);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Mark the deal signed - which hands the account to the IT team - or take that
   * mark back.
   *
   * Its own button rather than a field in the edit form: this is the one change
   * on this page that alters what another department can see, and that should
   * take a deliberate press, not a stray keystroke in a text box.
   */
  const setSigned = async (signed) => {
    setBusy(true);
    try {
      await customerApi.setSigned(id, signed);
      toast.success(signed ? t('customers.signedDone') : t('customers.unsignedDone'));
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (field, value) => {
    try {
      await customerApi.assign(id, { [field]: value || null });
      toast.success(t('customers.assign'));
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const peopleOptions = (role) =>
    (directory?.items ?? [])
      .filter((person) => person.role === role)
      .map((person) => ({ value: person.id, label: fullName(person) }));

  return (
    <>
      <PageHeader
        title={customer.companyName}
        subtitle={[customer.city, customer.country].filter(Boolean).join(', ')}
        actions={
          <div className="header-actions">
            <button type="button" className="btn" onClick={() => navigate(-1)}>
              {t('common.back')}
            </button>
            {canCall && !editing ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigate(`/telepro/qualification?customerId=${customer.id}`)}
              >
                {t('leads.call')}
              </button>
            ) : null}
            {customer.canSign && !editing ? (
              <button
                type="button"
                className={customer.signed ? 'btn' : 'btn btn--primary'}
                onClick={() => setSigned(!customer.signed)}
                disabled={busy}
              >
                {customer.signed ? t('customers.unsign') : t('customers.sign')}
              </button>
            ) : null}
            {customer.brief && !editing ? (
              <button type="button" className="btn" onClick={() => setShowQuestionnaire(true)}>
                {t('leads.questionnaire')}
              </button>
            ) : null}
            {canBill && !editing ? (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => navigate(`/documents/new?type=QUOTE&customerId=${customer.id}`)}
                >
                  {t('documents.newQuote')}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => navigate(`/documents/new?type=INVOICE&customerId=${customer.id}`)}
                >
                  {t('documents.newInvoice')}
                </button>
              </>
            ) : null}
            {canEdit && !editing ? (
              <button type="button" className="btn btn--primary" onClick={startEdit}>
                {t('common.edit')}
              </button>
            ) : null}
            {editing ? (
              <>
                <button type="button" className="btn" onClick={() => setEditing(false)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>
                  {busy ? t('common.saving') : t('common.save')}
                </button>
              </>
            ) : null}
          </div>
        }
      />

      {/* Landing here from the agenda, this is what the rep needs first: the
          summary and what we can propose. The question-by-question answers are
          behind the Questionnaire button - they are the reference you consult,
          not the thing you read on arrival, and inline they pushed everything
          else on this page below the fold. */}
      {customer.brief ? <LeadBrief brief={customer.brief} showAnswers={false} /> : null}

      <Modal
        open={showQuestionnaire}
        title={`${customer.companyName} - ${t('leads.questionnaire')}`}
        onClose={() => setShowQuestionnaire(false)}
        footer={
          <>
            {customer.briefLeadId ? (
              <button
                type="button"
                className="btn"
                onClick={() => navigate(`/leads/${customer.briefLeadId}`)}
              >
                {t('leads.openQualification')}
              </button>
            ) : null}
            <button type="button" className="btn btn--primary" onClick={() => setShowQuestionnaire(false)}>
              {t('common.close')}
            </button>
          </>
        }
      >
        {/* The whole thing: the synthesis AND every answer as it was given. */}
        <LeadBrief brief={customer.brief} />
      </Modal>

      <div className="grid grid--2">
        <Card title={t('customers.detail')}>
          {editing ? (
            <div className="form-grid">
              <Field label={t('common.status')} htmlFor="c-status">
                <Select
                  id="c-status"
                  value={draft.status}
                  onChange={(value) => setDraft({ ...draft, status: value })}
                  options={CUSTOMER_STATUSES.map((status) => ({ value: status, label: t(`customers.status.${status}`) }))}
                />
              </Field>
              <Field label={t('customers.estimatedValue')} htmlFor="c-value">
                <TextInput
                  id="c-value"
                  type="number"
                  value={draft.estimatedValue}
                  onChange={(value) => setDraft({ ...draft, estimatedValue: value })}
                />
              </Field>
              <Field label={t('customers.email')} htmlFor="c-email">
                <TextInput id="c-email" value={draft.email} onChange={(value) => setDraft({ ...draft, email: value })} />
              </Field>
              <Field label={t('customers.phone')} htmlFor="c-phone">
                <TextInput id="c-phone" value={draft.phone} onChange={(value) => setDraft({ ...draft, phone: value })} />
              </Field>
              <Field label={t('customers.city')} htmlFor="c-city">
                <TextInput id="c-city" value={draft.city} onChange={(value) => setDraft({ ...draft, city: value })} />
              </Field>
              <Field label={t('customers.country')} htmlFor="c-country">
                <TextInput
                  id="c-country"
                  value={draft.country}
                  onChange={(value) => setDraft({ ...draft, country: value })}
                />
              </Field>
              <div className="span-2">
                <Field label={t('common.notes')} htmlFor="c-notes">
                  <TextArea id="c-notes" value={draft.notes} onChange={(value) => setDraft({ ...draft, notes: value })} />
                </Field>
              </div>
            </div>
          ) : (
            <dl className="detail-list">
              <div>
                <dt>{t('common.status')}</dt>
                <dd>
                  <Badge value={customer.status} label={t(`customers.status.${customer.status}`)} />
                </dd>
              </div>
              <div>
                <dt>{t('customers.signed')}</dt>
                <dd>
                  <Badge
                    value={customer.signed ? 'SIGNED' : 'NOT_SIGNED'}
                    label={customer.signed ? t('common.yes') : t('customers.notSigned')}
                  />
                  {customer.signed && customer.signedAt ? (
                    <span className="cell-muted">
                      {' '}
                      {formatDate(customer.signedAt, locale)}
                      {customer.signedBy ? ` - ${fullName(customer.signedBy)}` : ''}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>{t('customers.contact')}</dt>
                <dd>{[customer.contactFirstName, customer.contactLastName].filter(Boolean).join(' ') || '-'}</dd>
              </div>
              <div>
                <dt>{t('customers.email')}</dt>
                <dd>{customer.email || '-'}</dd>
              </div>
              <div>
                <dt>{t('customers.phone')}</dt>
                <dd>{customer.phone || '-'}</dd>
              </div>
              <div>
                <dt>{t('customers.industry')}</dt>
                <dd>{customer.industry || '-'}</dd>
              </div>
              <div>
                <dt>{t('customers.companySize')}</dt>
                <dd>{customer.companySize || '-'}</dd>
              </div>
              <div>
                <dt>{t('customers.source')}</dt>
                <dd>{t(`customers.sources.${customer.source}`, customer.source)}</dd>
              </div>
              <div>
                <dt>{t('customers.estimatedValue')}</dt>
                <dd>{formatCurrency(customer.estimatedValue, i18n.resolvedLanguage, customer.currency)}</dd>
              </div>
              <div className="span-2">
                <dt>{t('common.notes')}</dt>
                <dd>{customer.notes || '-'}</dd>
              </div>
            </dl>
          )}
        </Card>

        {/* The paperwork on this account: quote it, invoice it, chase it - all
            from the record, because that is where you are when you decide to. */}
        {canBill ? (
          <Card title={t('documents.title')}>
            {docs?.items?.length ? (
              <ul className="timeline">
                {docs.items.map((doc) => (
                  <li key={doc.id} className="timeline__item">
                    <div className="timeline__main">
                      <Link to={`/documents/${doc.id}`} className="timeline__title cell-link">
                        {doc.number} - {t(`documents.types.${doc.type}`)}
                      </Link>
                      <p className="timeline__meta">
                        {formatCurrency(doc.total, locale, doc.currency)}
                        {doc.balance > 0 ? ` - ${t('receivables.balance')} ${formatCurrency(doc.balance, locale, doc.currency)}` : ''}
                        {doc.issueDate ? ` - ${formatDate(doc.issueDate, locale)}` : ''}
                      </p>
                    </div>
                    <div className="header-actions">
                      <Badge value={doc.status} label={t(`documents.statuses.${doc.status}`)} />
                      {doc.type === 'INVOICE' && doc.balance > 0 ? (
                        <button
                          type="button"
                          className="btn btn--small"
                          onClick={async () => {
                            try {
                              await documentApi.remind(doc.id);
                              toast.success(t('documents.reminded'));
                              refreshDocs();
                            } catch (err) {
                              toast.error(toMessage(err));
                            }
                          }}
                        >
                          {t('documents.remind')}
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title={t('documents.empty')} description={t('documents.emptyOnCustomer')} />
            )}
          </Card>
        ) : null}

        {/* The delivery work opened on this account, so the account answers
            "what is left to do here?" as well as "who is this?". */}
        {seesProjects ? (
          <Card title={t('projects.title')}>
            {projects?.items?.length ? (
              <ul className="timeline">
                {projects.items.map((project) => (
                  <li key={project.id} className="timeline__item">
                    <div className="timeline__main">
                      <Link to={`/it/projects?project=${project.id}`} className="timeline__title cell-link">
                        {project.code ? `${project.code} - ` : ''}
                        {project.title}
                      </Link>
                      <p className="timeline__meta">
                        {project.assignee ? fullName(project.assignee) : t('projects.unassigned')}
                        {project.dueAt ? ` - ${formatDate(project.dueAt, locale)}` : ''}
                        {project.taskCounts
                          ? ` - ${project.taskCounts.open}/${project.taskCounts.total} ${t('projects.openTasks').toLowerCase()}`
                          : ''}
                      </p>
                    </div>
                    <Badge
                      value={project.overdue ? 'LOST' : project.status}
                      label={project.overdue ? t('projects.overdue') : t(`projects.status.${project.status}`)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title={t('projects.empty')} />
            )}
          </Card>
        ) : null}

        {canAssign ? (
        <Card title={t('customers.assign')}>
          <div className="form-grid">
            <Field label={t('customers.telepro')} htmlFor="a-telepro">
              <Select
                id="a-telepro"
                value={customer.ownerTeleproId ?? ''}
                onChange={(value) => assign('ownerTeleproId', value)}
                placeholder={t('common.unassigned')}
                options={peopleOptions('TELEPRO')}
              />
            </Field>
            <Field label={t('customers.commercial')} htmlFor="a-commercial">
              <Select
                id="a-commercial"
                value={customer.ownerCommercialId ?? ''}
                onChange={(value) => assign('ownerCommercialId', value)}
                placeholder={t('common.unassigned')}
                options={peopleOptions('COMMERCIAL')}
              />
            </Field>
            <Field label={t('customers.itOwner')} htmlFor="a-it">
              <Select
                id="a-it"
                value={customer.itOwnerId ?? ''}
                onChange={(value) => assign('itOwnerId', value)}
                placeholder={t('common.unassigned')}
                options={peopleOptions('IT')}
              />
            </Field>
          </div>
        </Card>
        ) : null}

        <Card title={t('leads.title')}>
          {customer.leads?.length ? (
            <ul className="timeline">
              {customer.leads.map((lead) => (
                <li key={lead.id} className="timeline__item">
                  <div className="timeline__main">
                    <p className="timeline__title">{formatDate(lead.createdAt, i18n.resolvedLanguage)}</p>
                    <p className="timeline__meta">{lead.answers?.pain_description ?? ''}</p>
                  </div>
                  <ScoreGauge score={lead.score ?? 0} temperature={lead.temperature ?? 'COLD'} compact />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t('leads.empty')} />
          )}
        </Card>

        <Card title={t('agenda.title')}>
          {customer.appointments?.length ? (
            <ul className="timeline">
              {customer.appointments.map((appointment) => (
                <li key={appointment.id} className="timeline__item">
                  <div className="timeline__main">
                    <p className="timeline__title">{appointment.title}</p>
                    <p className="timeline__meta">{formatDateTime(appointment.startAt, i18n.resolvedLanguage)}</p>
                  </div>
                  <Badge value={appointment.status} label={t(`agenda.status.${appointment.status}`)} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t('agenda.noAppointments')} />
          )}
        </Card>
      </div>

      {/* What people said, above what the system recorded. */}
      <CommentThread customerId={id} highlightId={searchParams.get('comment')} />

      <Card title={t('customers.timeline')}>
        {activities?.items?.length ? (
          <ul className="timeline">
            {activities.items.map((activity) => (
              <li key={activity.id} className="timeline__item">
                <div className="timeline__main">
                  <p className="timeline__title">{activity.message}</p>
                  <p className="timeline__meta">
                    {formatDateTime(activity.createdAt, i18n.resolvedLanguage)} - {fullName(activity.author)}
                  </p>
                </div>
                <code className="timeline__code">{activity.type}</code>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('common.noData')} />
        )}
      </Card>
    </>
  );
};

export default CustomerDetailPage;
